/**
 * Strips the enum prefix from Google Ads API enum values
 * Examples:
 *   "CampaignStatus.ENABLED" -> "ENABLED"
 *   "AdvertisingChannelType.PERFORMANCE_MAX" -> "PERFORMANCE_MAX"
 *   "ENABLED" -> "ENABLED" (already formatted)
 */
export function stripEnumPrefix(value: string | undefined | null): string {
  if (!value) return '';

  // If the value contains a dot, take everything after the last dot
  const lastDotIndex = value.lastIndexOf('.');
  if (lastDotIndex !== -1) {
    return value.substring(lastDotIndex + 1);
  }

  return value;
}

/**
 * Sentinel status strings emitted by the Google Ads API + backend
 * normalizers (`convert_campaign_status` in campaigns.py,
 * `normalize_campaign_status` in enum_converters.py) when the source
 * value is missing or unparseable. Treating these as "missing" in
 * precedence chains avoids letting a sentinel shadow a meaningful
 * fallback (e.g. preferring `campaign_status: 'UNKNOWN'` over a valid
 * legacy `status: 'ENABLED'`, or persisting `'UNKNOWN'` over a valid
 * `existingCampaign.campaign_status`).
 */
const SENTINEL_STATUSES = new Set(['UNKNOWN', 'UNSPECIFIED']);

/**
 * Returns the canonical (stripped) status if it is a meaningful value,
 * otherwise `undefined` so it short-circuits in `||` / `??` precedence
 * chains. Always returns the stripped form on the meaningful path so the
 * helper stays self-consistent — it would be a leaky abstraction to
 * strip-then-check for sentinels but return the original (possibly
 * prefixed) value, since downstream code does strict bare-token
 * comparisons like `status === 'ENABLED'`. Matches the backend's
 * `normalize_campaign_status` / `convert_campaign_status`, which both
 * strip the prefix before returning.
 *
 *   meaningfulStatus('ENABLED')                  // 'ENABLED'
 *   meaningfulStatus('CampaignStatus.ENABLED')   // 'ENABLED'
 *   meaningfulStatus('UNKNOWN')                  // undefined
 *   meaningfulStatus('CampaignStatus.UNKNOWN')   // undefined
 *   meaningfulStatus('UNSPECIFIED')              // undefined
 *   meaningfulStatus(null)                       // undefined
 */
export function meaningfulStatus(
  value: string | undefined | null,
): string | undefined {
  if (!value) return undefined;
  const stripped = stripEnumPrefix(value);
  return SENTINEL_STATUSES.has(stripped) ? undefined : stripped;
}

/**
 * Formats an enum value for display by stripping prefix and replacing underscores with spaces
 * Examples:
 *   "CampaignStatus.ENABLED" -> "ENABLED"
 *   "AdvertisingChannelType.PERFORMANCE_MAX" -> "PERFORMANCE MAX"
 *
 * @deprecated Use formatEnumPretty() for better title-case formatting
 */
export function formatEnumForDisplay(value: string | undefined | null): string {
  const stripped = stripEnumPrefix(value);
  return stripped.replace(/_/g, ' ');
}

/**
 * Formats an enum value with smart title-case capitalization
 * - Strips enum prefix (e.g., "CampaignStatus.ENABLED" -> "Enabled")
 * - Converts underscores to spaces
 * - Keeps short words (2-4 chars) as uppercase acronyms (CPC, CPA, ROAS, Max)
 * - Title-cases longer words
 *
 * @param value - The enum value to format (can include prefix)
 * @returns Formatted string, or empty string if value is null/undefined
 *
 * @example
 * ```typescript
 * formatEnumPretty("PERFORMANCE_MAX")                    // "Performance Max"
 * formatEnumPretty("TARGET_CPA")                         // "Target CPA"
 * formatEnumPretty("MAXIMIZE_CONVERSION_VALUE")          // "Maximize Conversion Value"
 * formatEnumPretty("CampaignStatus.ENABLED")             // "Enabled"
 * formatEnumPretty("AdvertisingChannelType.SEARCH")      // "Search"
 * formatEnumPretty("LOWEST_COST_WITHOUT_CAP")            // "Lowest Cost Without Cap"
 * formatEnumPretty("LOWEST_COST_WITH_MIN_ROAS")          // "Lowest Cost With Min ROAS"
 * ```
 */
// Allowlist of ad-domain acronyms that should stay in all caps. A
// length-based heuristic ("2-4 letter words stay uppercase") looked
// neat in tests but mis-capitalized common English words used in
// Meta bid strategies — e.g. LOWEST_COST_WITHOUT_CAP became "Lowest
// COST Without CAP". Allowlist is the durable fix.
const ACRONYMS = new Set([
  // Performance metrics
  'CPC', 'CPA', 'CPM', 'CTR', 'CPL', 'CPS', 'CPE', 'CPV',
  'ROAS', 'ROI', 'KPI', 'AOV', 'LTV', 'VTR', 'VTC',
  // Platform / product
  'API', 'URL', 'UI', 'UX', 'SKU', 'CRM', 'CDN',
  // Google ad types & features
  'RSA', 'RDA', 'GSC', 'PMAX',
  // Meta features
  'CBO', 'ABO', 'DPA', 'FB', 'IG',
]);

export function formatEnumPretty(value: string | undefined | null): string {
  if (!value) return '';

  // Strip enum prefix if present
  const stripped = stripEnumPrefix(value);

  return stripped
    .split('_')
    .map(word => {
      const upper = word.toUpperCase();
      if (ACRONYMS.has(upper)) return upper;
      // Capitalize first letter, lowercase rest
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Formats a bidding strategy enum for display
 * Alias for formatEnumPretty with semantic naming
 *
 * @example
 * ```typescript
 * formatBiddingStrategy("TARGET_CPA")                  // "Target CPA"
 * formatBiddingStrategy("MAXIMIZE_CLICKS")             // "Maximize Clicks"
 * formatBiddingStrategy("MANUAL_CPC")                  // "Manual CPC"
 * ```
 */
export function formatBiddingStrategy(strategy: string | undefined | null): string {
  return formatEnumPretty(strategy);
}

/**
 * Formats a campaign type enum for display
 * Includes special handling for AI Max for Search campaigns
 *
 * @example
 * ```typescript
 * formatCampaignType("PERFORMANCE_MAX")                          // "Performance Max"
 * formatCampaignType("SEARCH")                                   // "Search"
 * formatCampaignType("AI_MAX_SEARCH")                            // "AI Max for Search"
 * formatCampaignType("AdvertisingChannelType.SHOPPING")          // "Shopping"
 * ```
 */
export function formatCampaignType(type: string | undefined | null): string {
  if (!type) return '';

  const stripped = stripEnumPrefix(type);

  // Special case for AI Max for Search
  if (stripped === 'AI_MAX_SEARCH') {
    return 'AI Max for Search';
  }

  // Special case for Demand Gen (cleaner than "Demand GEN")
  if (stripped === 'DEMAND_GEN') {
    return 'Demand Gen';
  }

  return formatEnumPretty(type);
}

/**
 * Formats network settings into an array of human-readable network names.
 *
 * @param settings - The NetworkSettings object from campaign data
 * @returns Array of network names that are enabled
 *
 * @example
 * ```typescript
 * formatNetworkSettings({
 *   target_google_search: true,
 *   target_search_network: true,
 *   target_content_network: false,
 *   target_partner_search_network: false
 * })
 * // Returns: ["Google Search", "Search Partners"]
 * ```
 */
export function formatNetworkSettings(settings: {
  target_google_search?: boolean;
  target_search_network?: boolean;
  target_content_network?: boolean;
  target_partner_search_network?: boolean;
} | undefined): string[] {
  if (!settings) return [];

  const networks: string[] = [];
  if (settings.target_google_search) networks.push('Google Search');
  if (settings.target_search_network) networks.push('Search Partners');
  if (settings.target_content_network) networks.push('Display Network');
  if (settings.target_partner_search_network) networks.push('Partner Search');
  return networks;
}
